import React, { useState, useMemo } from 'react';
import { DateAvailabilityInfo } from '../../services/tripService';
import { ChevronRight, ChevronLeft, Calendar as CalendarIcon, X, Check, AlertCircle } from 'lucide-react';
import './MultiDayCalendar.css';

export interface MultiDayCalendarProps {
  /** Map of ISO date "YYYY-MM-DD" to availability metadata */
  availabilityMap: Record<string, DateAvailabilityInfo>;
  /** Selected ISO dates */
  selectedDates: string[];
  /** Callback when dates selection changes */
  onSelectDates: (dates: string[]) => void;
  /** Max dates selectable (default: 14) */
  maxSelectable?: number;
  /** Single date mode (if mode is SINGLE) */
  singleSelection?: boolean;
  /** Callback when user clicks Continue */
  onContinue?: () => void;
  /** Optional direction indicator for title/context */
  directionLabel?: string;
  /** Minimum selectable date (defaults to today) */
  minDate?: string;
}

export const formatLocalDate = (year: number, monthZeroIndexed: number, day: number): string => {
  const m = String(monthZeroIndexed + 1).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${year}-${m}-${d}`;
};

export const getTodayLocalIso = (): string => {
  const now = new Date();
  return formatLocalDate(now.getFullYear(), now.getMonth(), now.getDate());
};

export const MultiDayCalendar: React.FC<MultiDayCalendarProps> = ({
  availabilityMap,
  selectedDates,
  onSelectDates,
  maxSelectable = 14,
  singleSelection = false,
  onContinue,
  directionLabel,
  minDate,
}) => {
  const todayIso = useMemo(() => getTodayLocalIso(), []);
  const effectiveMinDate = minDate || todayIso;

  // Initial month view is current month of earliest selected date or today
  const [currentMonthDate, setCurrentMonthDate] = useState<Date>(() => {
    if (selectedDates.length > 0) {
      const earliest = [...selectedDates].sort()[0];
      const parts = earliest.split('-');
      if (parts.length === 3) {
        return new Date(Number(parts[0]), Number(parts[1]) - 1, 1);
      }
    }
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const year = currentMonthDate.getFullYear();
  const month = currentMonthDate.getMonth(); // 0-indexed

  // Month navigation
  const prevMonth = () => {
    setCurrentMonthDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonthDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  // Prevent navigating into the past before current month
  const isPrevDisabled = useMemo(() => {
    const parts = effectiveMinDate.split('-');
    if (parts.length === 3) {
      const minMonthDate = new Date(Number(parts[0]), Number(parts[1]) - 1, 1);
      return currentMonthDate <= minMonthDate;
    }
    return false;
  }, [currentMonthDate, effectiveMinDate]);

  // Calendar matrix calculation
  const calendarDays = useMemo(() => {
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDayOfMonth.getDay(); // 0 = Sun, 1 = Mon, ..., 6 = Sat
    const totalDaysInMonth = lastDayOfMonth.getDate();

    const days: Array<{
      dateStr: string;
      dayNumber: number;
      isCurrentMonth: boolean;
      isToday: boolean;
      isPast: boolean;
      status: 'AVAILABLE' | 'FULL' | 'NO_TRIP' | 'PAST';
      minPrice?: number;
      availCount?: number;
    }> = [];

    // Previous month filler days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    const prevYear = month === 0 ? year - 1 : year;
    const prevMonthVal = month === 0 ? 11 : month - 1;
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const dayNum = prevMonthLastDay - i;
      const dateStr = formatLocalDate(prevYear, prevMonthVal, dayNum);
      days.push({
        dateStr,
        dayNumber: dayNum,
        isCurrentMonth: false,
        isToday: dateStr === todayIso,
        isPast: dateStr < effectiveMinDate,
        status: 'PAST',
      });
    }

    // Current month days
    for (let d = 1; d <= totalDaysInMonth; d++) {
      const dateStr = formatLocalDate(year, month, d);
      const isPast = dateStr < effectiveMinDate;

      let status: 'AVAILABLE' | 'FULL' | 'NO_TRIP' | 'PAST' = 'NO_TRIP';
      let minPrice: number | undefined;
      let availCount: number | undefined;

      if (isPast) {
        status = 'PAST';
      } else {
        const info = availabilityMap[dateStr];
        if (info) {
          status = info.status;
          minPrice = info.minPrice;
          availCount = info.availableSeatsCount;
        } else {
          status = 'NO_TRIP';
        }
      }

      days.push({
        dateStr,
        dayNumber: d,
        isCurrentMonth: true,
        isToday: dateStr === todayIso,
        isPast,
        status,
        minPrice,
        availCount,
      });
    }

    // Next month filler days to complete 35 or 42 grid cells
    const remaining = (7 - (days.length % 7)) % 7;
    const nextYear = month === 11 ? year + 1 : year;
    const nextMonthVal = month === 11 ? 0 : month + 1;
    for (let i = 1; i <= remaining; i++) {
      const dateStr = formatLocalDate(nextYear, nextMonthVal, i);
      days.push({
        dateStr,
        dayNumber: i,
        isCurrentMonth: false,
        isToday: false,
        isPast: false,
        status: 'NO_TRIP',
      });
    }

    return days;
  }, [year, month, todayIso, effectiveMinDate, availabilityMap]);

  // Month title in Arabic and English
  const monthTitle = useMemo(() => {
    return currentMonthDate.toLocaleDateString('ar-EG', {
      month: 'long',
      year: 'numeric',
    });
  }, [currentMonthDate]);

  // Date selection handler
  const handleDateClick = (dateStr: string, status: string, isPast: boolean) => {
    if (isPast || status === 'NO_TRIP' || status === 'PAST') {
      return;
    }

    if (singleSelection) {
      onSelectDates([dateStr]);
      return;
    }

    if (selectedDates.includes(dateStr)) {
      // Deselect
      onSelectDates(selectedDates.filter((d) => d !== dateStr));
    } else {
      // Select (if limit not exceeded)
      if (selectedDates.length >= maxSelectable) {
        return;
      }
      const updated = [...selectedDates, dateStr].sort();
      onSelectDates(updated);
    }
  };

  const handleClearAll = () => {
    onSelectDates([]);
  };

  const handleRemoveDate = (dateToRemove: string) => {
    onSelectDates(selectedDates.filter((d) => d !== dateToRemove));
  };

  // Days of week header in Arabic
  const weekDays = ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'];

  return (
    <div className="multi-day-calendar-card" dir="rtl">
      {/* Calendar Header with Navigation */}
      <div className="calendar-header-bar">
        <div className="month-title-wrap">
          <CalendarIcon size={20} className="text-blue-600" />
          <h3 className="month-heading">{monthTitle}</h3>
          {directionLabel && <span className="direction-badge">{directionLabel}</span>}
        </div>

        <div className="month-nav-controls">
          <button
            type="button"
            className="month-nav-btn"
            onClick={prevMonth}
            disabled={isPrevDisabled}
            title="الشهر السابق"
            aria-label="الشهر السابق"
          >
            <ChevronRight size={20} />
          </button>
          <button
            type="button"
            className="month-nav-btn"
            onClick={nextMonth}
            title="الشهر القادم"
            aria-label="الشهر القادم"
          >
            <ChevronLeft size={20} />
          </button>
        </div>
      </div>

      {/* Weekday Labels Header */}
      <div className="calendar-weekdays-row" aria-hidden="true">
        {weekDays.map((wd, i) => (
          <div key={i} className="weekday-col">
            {wd}
          </div>
        ))}
      </div>

      {/* Calendar Days Grid */}
      <div className="calendar-days-grid" role="grid" aria-label="أيام الشهر">
        {calendarDays.map((item, index) => {
          if (!item.isCurrentMonth) {
            return (
              <div key={index} className="calendar-day-cell outside-month" aria-disabled="true">
                <span className="day-number text-slate-300">{item.dayNumber}</span>
              </div>
            );
          }

          const isSelected = selectedDates.includes(item.dateStr);
          const isSelectable = !item.isPast && item.status === 'AVAILABLE';
          const isFull = !item.isPast && item.status === 'FULL';
          const isNoTrip = !item.isPast && item.status === 'NO_TRIP';

          let cellClassName = 'calendar-day-cell';
          if (item.isToday) cellClassName += ' is-today';
          if (isSelected) cellClassName += ' is-selected';
          if (item.isPast) cellClassName += ' is-past';
          if (isFull) cellClassName += ' is-full';
          if (isNoTrip) cellClassName += ' is-no-trip';
          if (isSelectable) cellClassName += ' is-available';

          return (
            <button
              key={index}
              type="button"
              className={cellClassName}
              disabled={item.isPast || isNoTrip}
              onClick={() => handleDateClick(item.dateStr, item.status, item.isPast)}
              aria-label={`${item.dateStr}, ${item.status}`}
              aria-pressed={isSelected}
            >
              <div className="day-top-row">
                <span className="day-number font-mono">{item.dayNumber}</span>
                {item.isToday && <span className="today-dot" title="اليوم"></span>}
              </div>

              {/* Status and Price Indicator */}
              <div className="day-status-pill">
                {isSelected ? (
                  <span className="selected-indicator">
                    <Check size={12} strokeWidth={3} />
                  </span>
                ) : item.isPast ? (
                  <span className="status-label past">مضى</span>
                ) : isFull ? (
                  <span className="status-label full">مكتمل</span>
                ) : isNoTrip ? (
                  <span className="status-label no-trip">—</span>
                ) : (
                  <span className="status-label available">
                    {item.minPrice ? `${item.minPrice} ج.م` : 'متاح'}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="calendar-legend-bar">
        <div className="legend-item">
          <span className="legend-dot available"></span>
          <span>رحلات متاحة</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot selected"></span>
          <span>محدد</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot full"></span>
          <span>مكتمل</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot no-trip"></span>
          <span>لا توجد رحلات</span>
        </div>
      </div>

      {/* Selected Dates Summary Bar */}
      <div className="selected-dates-summary-box">
        <div className="summary-header-row">
          <div className="summary-count-group">
            <strong>الأيام المحددة للحجز:</strong>
            <span className="count-badge font-mono">
              {selectedDates.length} {selectedDates.length === 1 ? 'يوم' : 'أيام'}
            </span>
            {selectedDates.length > 0 && (
              <span className="text-xs text-slate-500">
                (يمكنك النقر على أي تاريخ لإلغاء تحديده)
              </span>
            )}
          </div>

          {selectedDates.length > 0 && (
            <button
              type="button"
              className="btn-clear-all"
              onClick={handleClearAll}
              title="مسح كافة الأيام المحددة"
            >
              مسح الكل
            </button>
          )}
        </div>

        {selectedDates.length === 0 ? (
          <div className="no-selection-hint">
            <AlertCircle size={16} className="text-blue-500" />
            <span>اضغط على الأيام الخضراء في التقويم لتحديد أيام سفرك</span>
          </div>
        ) : (
          <div className="selected-chips-scroll">
            {selectedDates.map((dStr) => {
              const dObj = new Date(dStr);
              const label = dObj.toLocaleDateString('ar-EG', {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
              });

              return (
                <div key={dStr} className="selected-date-chip">
                  <span className="chip-text">{label}</span>
                  <button
                    type="button"
                    className="chip-remove-btn"
                    onClick={() => handleRemoveDate(dStr)}
                    title={`إلغاء ${label}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Action Button */}
        {onContinue && (
          <div className="calendar-action-bar">
            <button
              type="button"
              className="btn-continue-calendar"
              disabled={selectedDates.length === 0}
              onClick={onContinue}
            >
              <span>متابعة لاختيار الرحلات والمقاعد ({selectedDates.length})</span>
              <Check size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
